// Shared cookie-consent model. Stored in a first-party cookie (not
// localStorage) so API routes can honor the visitor's choice too — e.g. the
// HubSpot `hutk` forwarding in /api/demo is gated on this record.

export const CONSENT_COOKIE = 'ocx_consent';

// Bump when the cookie policy changes materially — stored consents made
// against an older version stop counting and the banner re-prompts.
export const CONSENT_POLICY_VERSION = 1;

export const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // 6 months

export interface ConsentCategories {
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentRecord extends ConsentCategories {
  /** Policy version the choice was made against. */
  v: number;
  /** Unix ms timestamp of the choice — the Art. 7(1) GDPR consent record. */
  ts: number;
}

// Countries where non-essential cookies require prior opt-in consent:
// EEA (GDPR + ePrivacy), UK + Gibraltar (UK GDPR/PECR), Switzerland (FADP).
export const OPT_IN_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
  'GI',
  'CH',
];

/**
 * Whether the visitor's jurisdiction requires opt-in before non-essential
 * cookies. Unknown geo (stripped headers, some proxies, localhost) gets the
 * opt-in treatment: privacy-by-default beats guessing wrong for an EU visitor.
 */
export function requiresOptIn(countryCode: string | null | undefined): boolean {
  if (!countryCode) return true;
  return OPT_IN_COUNTRIES.includes(countryCode.toUpperCase());
}

/** Parse the consent record out of a Cookie header (or document.cookie). */
export function parseConsentCookie(
  cookieHeader: string | null | undefined,
): ConsentRecord | null {
  const raw = cookieHeader?.match(
    new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]+)`),
  )?.[1];
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      typeof parsed.analytics !== 'boolean' ||
      typeof parsed.marketing !== 'boolean' ||
      parsed.v !== CONSENT_POLICY_VERSION
    ) {
      return null;
    }
    return parsed as ConsentRecord;
  } catch {
    return null;
  }
}

/**
 * Server-side check for API routes: whether this request's cookies show
 * effective analytics consent (explicit choice, or opt-out-jurisdiction
 * default). Used to decide if HubSpot's `hutk` may be forwarded on form
 * submissions — joining browsing history to a contact record needs the same
 * consent as the tracking that produced it.
 */
export function hasAnalyticsConsent(
  cookieHeader: string | null | undefined,
): boolean {
  const record = parseConsentCookie(cookieHeader);
  if (record) return record.analytics;
  const country = cookieHeader?.match(/(?:^|;\s*)VISITOR_COUNTRY=([^;]+)/)?.[1];
  return !requiresOptIn(country);
}

export function serializeConsent(categories: ConsentCategories): string {
  const record: ConsentRecord = {
    v: CONSENT_POLICY_VERSION,
    ts: Date.now(),
    analytics: categories.analytics,
    marketing: categories.marketing,
  };
  return encodeURIComponent(JSON.stringify(record));
}
